import React, { useState } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { valuationAPI } from '../lib/api';
import { toast } from 'sonner';
import {
  Calculator,
  TrendingUp,
  MapPin,
  Calendar,
  Loader2,
  ArrowRight,
  Info,
} from 'lucide-react';

const ValuationPage = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [formData, setFormData] = useState({
    size_sqm: '',
    land_type: 'residential',
    city: 'Gaborone',
    has_utilities: false,
    road_access: true,
    zoning: '',
  });

  const cities = ['Gaborone', 'Francistown', 'Maun', 'Kasane', 'Palapye', 'Serowe', 'Selebi-Phikwe', 'Lobatse'];

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleSwitchChange = (name, checked) => {
    setFormData({ ...formData, [name]: checked });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.size_sqm || parseFloat(formData.size_sqm) <= 0) {
      toast.error('Please enter a valid land size');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const data = {
        ...formData,
        size_sqm: parseFloat(formData.size_sqm),
        zoning: formData.zoning === 'none' ? '' : formData.zoning,
      };
      const response = await valuationAPI.estimate(data);
      setResult(response.data);
      toast.success('Valuation complete!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Valuation failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-BW', {
      style: 'currency',
      currency: 'BWP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="valuation-page">
        {/* Header */}
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold">Land Valuation</h1>
          <p className="text-muted-foreground mt-1">
            Get an instant estimate of your land's market value
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Form */}
          <Card className="border border-border">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Calculator className="w-5 h-5" />
                Valuation Calculator
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="size_sqm">Land Size (sqm) *</Label>
                    <Input
                      id="size_sqm"
                      name="size_sqm"
                      type="number"
                      value={formData.size_sqm}
                      onChange={handleInputChange}
                      placeholder="e.g., 1200"
                      required
                      min="1"
                      data-testid="valuation-size-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="city">City *</Label>
                    <Select 
                      value={formData.city} 
                      onValueChange={(v) => handleSelectChange('city', v)}
                    >
                      <SelectTrigger data-testid="valuation-city-select">
                        <SelectValue placeholder="Select city" />
                      </SelectTrigger>
                      <SelectContent>
                        {cities.map((city) => (
                          <SelectItem key={city} value={city}>{city}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="land_type">Land Type *</Label>
                  <Select 
                    value={formData.land_type} 
                    onValueChange={(v) => handleSelectChange('land_type', v)}
                  >
                    <SelectTrigger data-testid="valuation-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="residential">Residential</SelectItem>
                      <SelectItem value="commercial">Commercial</SelectItem>
                      <SelectItem value="industrial">Industrial</SelectItem>
                      <SelectItem value="agricultural">Agricultural</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="zoning">Zoning Status</Label>
                  <Select 
                    value={formData.zoning || ''} 
                    onValueChange={(v) => handleSelectChange('zoning', v)}
                  >
                    <SelectTrigger data-testid="valuation-zoning-select">
                      <SelectValue placeholder="Select zoning status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not specified</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="not_applied">Not Applied</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-4 pt-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="has_utilities">Connected to Utilities</Label>
                      <p className="text-xs text-muted-foreground">
                        Water, electricity, sewage
                      </p>
                    </div>
                    <Switch
                      id="has_utilities"
                      checked={formData.has_utilities}
                      onCheckedChange={(c) => handleSwitchChange('has_utilities', c)}
                      data-testid="utilities-switch"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="road_access">Road Access</Label>
                      <p className="text-xs text-muted-foreground">
                        Direct access to paved road
                      </p>
                    </div>
                    <Switch
                      id="road_access"
                      checked={formData.road_access}
                      onCheckedChange={(c) => handleSwitchChange('road_access', c)}
                      data-testid="road-switch"
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={loading}
                  data-testid="calculate-valuation-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Calculating...
                    </>
                  ) : (
                    <>
                      Calculate Value
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Result */}
          <div className="space-y-6">
            {result ? (
              <>
                <Card className="border border-secondary bg-secondary/5">
                  <CardContent className="p-6">
                    <p className="text-sm text-muted-foreground uppercase tracking-wide mb-2">
                      Estimated Value
                    </p>
                    <p className="font-heading text-4xl sm:text-5xl font-bold text-secondary" data-testid="valuation-result">
                      {formatCurrency(result.estimated_value)}
                    </p>
                    <div className="flex items-center gap-4 mt-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {formData.city}
                      </div>
                      <div>
                        {formatCurrency(result.price_per_sqm)}/sqm
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border border-border">
                  <CardHeader>
                    <CardTitle className="font-heading text-lg flex items-center gap-2">
                      <TrendingUp className="w-5 h-5" />
                      Market Insights
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-muted/30 rounded-sm">
                      <span className="text-muted-foreground">Market Trend</span>
                      <span className="font-medium capitalize">{result.market_trend}</span>
                    </div>

                    <div>
                      <p className="text-sm font-medium mb-3">Comparable Sales</p>
                      <div className="space-y-2">
                        {result.comparable_sales?.map((sale, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between p-3 bg-muted/30 rounded-sm text-sm"
                          >
                            <div>
                              <p className="font-medium">{sale.address}</p>
                              <p className="text-xs text-muted-foreground">
                                {Math.round(sale.size_sqm).toLocaleString()} sqm
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-mono font-medium">
                                {formatCurrency(sale.sale_price)}
                              </p>
                              <p className="text-xs text-muted-foreground flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {new Date(sale.sale_date).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card className="border border-border">
                <CardContent className="p-12 text-center">
                  <Calculator className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-heading font-bold text-lg mb-2">
                    Enter Land Details
                  </h3>
                  <p className="text-muted-foreground">
                    Fill in the form to get an instant valuation estimate
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Disclaimer */}
            <Card className="border border-amber-200 bg-amber-50">
              <CardContent className="p-4 flex gap-3">
                <Info className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium mb-1">Disclaimer</p>
                  <p>
                    This is an estimated value based on market data and may differ from 
                    actual market value. For official valuations, please consult a 
                    certified property valuer.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ValuationPage;
